import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import {
  Image as ImageIcon,
  Trash2,
  Upload,
  Star,
  X,
  Tag,
  GripVertical,
  Save,
  CheckSquare,
  Square,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
} from "lucide-react";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Event {
  id: number;
  title: string;
  event_date: string | null;
}

interface GalleryItem {
  id: number;
  event_id: number;
  image_url: string;
  caption: string | null;
  subject: string | null;
  is_featured: boolean;
  sort_order: number;
}

// ── Drag-to-reorder hook ──────────────────────────────────────────────────────

function useDragOrder(initialItems: GalleryItem[]) {
  const [items, setItems] = useState<GalleryItem[]>(initialItems);
  const [dirty, setDirty] = useState(false);
  const dragIndex = useRef<number | null>(null);

  useEffect(() => {
    setItems(initialItems);
    setDirty(false);
  }, [initialItems]);

  const onDragStart = useCallback((index: number) => {
    dragIndex.current = index;
  }, []);

  const onDragOver = useCallback((e: React.DragEvent, overIndex: number) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === overIndex) return;
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex.current!, 1);
      next.splice(overIndex, 0, moved);
      dragIndex.current = overIndex;
      return next;
    });
    setDirty(true);
  }, []);

  const onDragEnd = useCallback(() => {
    dragIndex.current = null;
  }, []);

  const reset = useCallback(() => {
    setItems(initialItems);
    setDirty(false);
  }, [initialItems]);

  /** Move item at `fromIndex` to `toIndex` (1-based position from user input) */
  const moveToPosition = useCallback((fromIndex: number, toPosition: number) => {
    setItems((prev) => {
      const clampedPos = Math.max(1, Math.min(prev.length, toPosition));
      const toIndex = clampedPos - 1;
      if (toIndex === fromIndex) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setDirty(true);
  }, []);

  return { items, dirty, reset, onDragStart, onDragOver, onDragEnd, moveToPosition };
}

// ── Subject group with drag & reorder ────────────────────────────────────────

interface SubjectGroupProps {
  subject: string;
  initialItems: GalleryItem[];
  selectedIds: Set<number>;
  defaultOpen?: boolean;
  /** Position of this subject in the subject list (1-based, for the order input) */
  subjectPosition: number;
  totalSubjects: number;
  subjectIndex: number;
  isDraggingSubject: boolean;
  onSubjectDragStart: (index: number) => void;
  onSubjectDragOver: (e: React.DragEvent, index: number) => void;
  onSubjectDragEnd: () => void;
  /** Called when the admin types a new position number for this subject */
  onSubjectMoveToPosition: (fromIndex: number, toPosition: number) => void;
  onToggleSelect: (id: number) => void;
  onToggleFeatured: (item: GalleryItem) => void;
  onDelete: (id: number) => void;
  onUpdateSubject: (item: GalleryItem, value: string) => void;
  onUpdateCaption: (item: GalleryItem, value: string) => void;
  onSaveOrder: (items: GalleryItem[]) => Promise<void>;
  saving: boolean;
}

function SubjectGroup({
  subject,
  initialItems,
  selectedIds,
  defaultOpen = true,
  subjectPosition,
  totalSubjects,
  subjectIndex,
  isDraggingSubject,
  onSubjectDragStart,
  onSubjectDragOver,
  onSubjectDragEnd,
  onSubjectMoveToPosition,
  onToggleSelect,
  onToggleFeatured,
  onDelete,
  onUpdateSubject,
  onUpdateCaption,
  onSaveOrder,
  saving,
}: SubjectGroupProps) {
  const { items, dirty, reset, onDragStart, onDragOver, onDragEnd, moveToPosition } =
    useDragOrder(initialItems);

  const [open, setOpen] = useState(defaultOpen);
  // Local value for the subject position input
  const [subjectPosInput, setSubjectPosInput] = useState(String(subjectPosition));

  // Keep subject position input in sync when order changes externally
  useEffect(() => {
    setSubjectPosInput(String(subjectPosition));
  }, [subjectPosition]);

  const allSelected = items.length > 0 && items.every((i) => selectedIds.has(i.id));
  const someSelected = items.some((i) => selectedIds.has(i.id));
  const selectedCount = items.filter((i) => selectedIds.has(i.id)).length;

  const handleToggleAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    items.forEach((i) => {
      const isSelected = selectedIds.has(i.id);
      if (allSelected ? isSelected : !isSelected) onToggleSelect(i.id);
    });
  };

  const commitSubjectPosition = () => {
    const n = parseInt(subjectPosInput, 10);
    if (!isNaN(n) && n !== subjectPosition) {
      onSubjectMoveToPosition(subjectIndex, n);
    } else {
      // Reset to current if invalid
      setSubjectPosInput(String(subjectPosition));
    }
  };

  return (
    <div
      onDragOver={(e) => onSubjectDragOver(e, subjectIndex)}
      className={[
        "rounded-xl border overflow-hidden transition-all duration-150",
        isDraggingSubject ? "border-primary/40 shadow-md" : "border-border",
      ].join(" ")}
    >
      {/* ── Accordion header ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-3 py-2 bg-secondary/50 hover:bg-secondary transition-colors">

        {/* Subject order number input */}
        <div className="flex items-center gap-1 flex-shrink-0" title="Subject position — type a number to reorder">
          <input
            type="number"
            min={1}
            max={totalSubjects}
            value={subjectPosInput}
            onChange={(e) => setSubjectPosInput(e.target.value)}
            onBlur={commitSubjectPosition}
            onKeyDown={(e) => { if (e.key === "Enter") { (e.target as HTMLInputElement).blur(); } }}
            onClick={(e) => e.stopPropagation()}
            className="w-10 rounded border border-border bg-background text-center text-xs font-mono tabular-nums py-0.5 focus:outline-none focus:ring-1 focus:ring-primary"
            aria-label={`Subject position for ${subject}`}
          />
        </div>

        {/* Subject drag handle */}
        <div
          draggable
          onDragStart={(e) => { e.stopPropagation(); onSubjectDragStart(subjectIndex); }}
          onDragEnd={(e) => { e.stopPropagation(); onSubjectDragEnd(); }}
          className="flex-shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-primary transition-colors p-1 rounded hover:bg-black/5"
          title="Drag to reorder this subject"
        >
          <GripVertical className="w-4 h-4" />
        </div>

        {/* Chevron + label */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 flex-1 min-w-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset rounded"
          aria-expanded={open}
        >
          <ChevronDown
            className={[
              "w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform duration-200",
              open ? "rotate-0" : "-rotate-90",
            ].join(" ")}
          />
          <Tag className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="font-medium text-sm text-primary flex-1 truncate">{subject}</span>
        </button>

        {/* Badges */}
        <span className="text-xs text-muted-foreground flex-shrink-0 tabular-nums">
          {items.length} {items.length === 1 ? "photo" : "photos"}
        </span>
        {selectedCount > 0 && (
          <span className="flex-shrink-0 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full tabular-nums">
            {selectedCount} selected
          </span>
        )}
        {dirty && (
          <span className="flex-shrink-0 bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">
            unsaved order
          </span>
        )}

        {/* Select-all */}
        <button
          type="button"
          onClick={handleToggleAll}
          className="flex-shrink-0 text-muted-foreground hover:text-primary transition-colors p-1 rounded hover:bg-black/5"
          title={allSelected ? "Deselect all in group" : "Select all in group"}
        >
          {allSelected ? (
            <CheckSquare className="w-4 h-4 text-primary" />
          ) : someSelected ? (
            <CheckSquare className="w-4 h-4 text-primary/50" />
          ) : (
            <Square className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* ── Accordion body ────────────────────────────────────────────────── */}
      {open && (
        <div className="p-4 space-y-4 bg-background">
          {dirty && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <span className="text-sm text-amber-700 flex-1">
                Order changed — save to persist.
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={reset}
                disabled={saving}
                className="text-amber-700 hover:text-amber-900 hover:bg-amber-100"
              >
                Reset
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => onSaveOrder(items)}
                disabled={saving}
                className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Save className="w-3.5 h-3.5" />
                {saving ? "Saving…" : "Save Order"}
              </Button>
            </div>
          )}

          {items.length > 1 && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <GripVertical className="w-3 h-3" />
              Drag images or type a position number to reorder
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item, index) => {
              const isSelected = selectedIds.has(item.id);
              return (
                <div
                  key={item.id}
                  draggable
                  onDragStart={() => onDragStart(index)}
                  onDragOver={(e) => onDragOver(e, index)}
                  onDragEnd={onDragEnd}
                  className={[
                    "space-y-2 rounded-xl p-1 transition-all",
                    isSelected ? "bg-primary/5 ring-2 ring-primary/30" : "hover:bg-secondary/30",
                    "cursor-grab active:cursor-grabbing",
                  ].join(" ")}
                >
                  {/* Image tile */}
                  <div className="relative aspect-square rounded-lg overflow-hidden bg-secondary group">
                    <img
                      src={item.image_url}
                      alt={item.caption || "Gallery image"}
                      className="w-full h-full object-cover"
                      draggable={false}
                    />
                    <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="bg-black/50 rounded p-1">
                        <GripVertical className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onToggleSelect(item.id); }}
                      className="absolute top-2 left-10 bg-white/90 rounded p-0.5 shadow transition-opacity opacity-0 group-hover:opacity-100 data-[selected=true]:opacity-100"
                      data-selected={isSelected}
                      title="Select image"
                    >
                      {isSelected ? (
                        <CheckSquare className="w-4 h-4 text-primary" />
                      ) : (
                        <Square className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>
                    <div className="absolute top-2 right-2 flex gap-1">
                      <Button type="button" variant="secondary" size="icon" className="h-8 w-8"
                        onClick={() => onToggleFeatured(item)}
                        title={item.is_featured ? "Remove from featured" : "Mark as featured"}>
                        <Star className={`w-4 h-4 ${item.is_featured ? "fill-yellow-400 text-yellow-400" : ""}`} />
                      </Button>
                      <Button type="button" variant="destructive" size="icon" className="h-8 w-8"
                        onClick={() => onDelete(item.id)} title="Delete image">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {item.is_featured && (
                      <div className="absolute bottom-2 left-2 bg-yellow-400 text-black px-2 py-0.5 rounded-full text-xs font-medium">
                        Featured
                      </div>
                    )}
                  </div>

                  {/* Order number input + position badge */}
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground whitespace-nowrap">
                      Position
                    </Label>
                    <input
                      type="number"
                      min={1}
                      max={items.length}
                      defaultValue={index + 1}
                      key={`${item.id}-${index}`}
                      onBlur={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (!isNaN(n) && n !== index + 1) {
                          moveToPosition(index, n);
                        } else {
                          e.target.value = String(index + 1);
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                      onClick={(e) => e.stopPropagation()}
                      className="w-14 rounded border border-border bg-background text-center text-sm font-mono tabular-nums py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                      aria-label={`Position of image ${index + 1}`}
                    />
                    <span className="text-xs text-muted-foreground">of {items.length}</span>
                  </div>

                  {/* Subject field */}
                  <div>
                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                      <Tag className="w-3 h-3" /> Subject
                    </Label>
                    <Input
                      list="subject-suggestions"
                      defaultValue={item.subject || ""}
                      onBlur={(e) => {
                        if (e.target.value !== (item.subject || "")) {
                          onUpdateSubject(item, e.target.value);
                        }
                      }}
                      placeholder="e.g. Networking"
                      className="text-sm mt-0.5"
                    />
                  </div>

                  {/* Caption field */}
                  <Textarea
                    defaultValue={item.caption || ""}
                    onBlur={(e) => {
                      if (e.target.value !== (item.caption || "")) {
                        onUpdateCaption(item, e.target.value);
                      }
                    }}
                    placeholder="Add caption…"
                    rows={2}
                    className="text-sm"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function GalleryEditor() {
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);

  // Selection state for bulk operations
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // New single-image form
  const [newItem, setNewItem] = useState({
    image_url: "",
    caption: "",
    subject: "",
    is_featured: false,
  });

  // Subject for bulk uploads
  const [bulkSubject, setBulkSubject] = useState("");

  // Existing subjects for datalist autocomplete
  const [existingSubjects, setExistingSubjects] = useState<string[]>([]);

  const showToast = (message: string, type: "success" | "error" = "success") => {
    alert(`${type === "success" ? "✅" : "❌"} ${message}`);
  };

  useEffect(() => { loadEvents(); }, []);

  useEffect(() => {
    if (selectedEventId) {
      loadGallery(selectedEventId);
      loadSubjects(selectedEventId);
      setSelectedIds(new Set());
    }
  }, [selectedEventId]);

  // ── Data loaders ─────────────────────────────────────────────────────────────

  const loadEvents = async () => {
    try {
      const response = await api.get<Event[]>("/api/events");
      if (response.success && response.data) {
        setEvents(response.data);
        if (response.data.length > 0 && !selectedEventId) {
          setSelectedEventId(response.data[0].id);
        }
      }
    } catch {
      showToast("Failed to load events", "error");
    }
  };

  const loadGallery = async (eventId: number) => {
    try {
      setLoading(true);
      const response = await api.get(`/api/gallery/event/${eventId}`);
      if (response.success && response.data) {
        setGalleryItems((response.data as any).gallery_items || []);
      }
    } catch {
      showToast("Failed to load gallery", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadSubjects = async (eventId: number) => {
    try {
      const response = await api.get<string[]>(`/api/gallery/event/${eventId}/subjects`);
      if (response.success && response.data) {
        setExistingSubjects(response.data.filter((s) => s !== "General"));
      }
    } catch {
      setExistingSubjects([]);
    }
  };

  // ── Selection helpers ─────────────────────────────────────────────────────────

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(galleryItems.map((i) => i.id)));
  const clearSelection = () => setSelectedIds(new Set());

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const urls = await Promise.all(
        Array.from(files).map(async (file) => {
          const formData = new FormData();
          formData.append("image", file);
          const res = await fetch(
            `${import.meta.env.VITE_API_URL || "http://localhost:3001"}/api/upload`,
            { method: "POST", body: formData }
          );
          const result = await res.json();
          if (result.success && result.data?.imageUrl) return result.data.imageUrl as string;
          throw new Error("Upload failed");
        })
      );
      setUploadedImages((prev) => [...prev, ...urls]);
      showToast(`${urls.length} image(s) uploaded successfully`);
    } catch {
      showToast("Failed to upload images", "error");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleAddItem = async () => {
    if (!selectedEventId || !newItem.image_url) {
      showToast("Please select an event and provide an image URL", "error");
      return;
    }
    setLoading(true);
    try {
      const response = await api.post("/api/gallery", {
        event_id: selectedEventId,
        image_url: newItem.image_url,
        caption: newItem.caption || null,
        subject: newItem.subject.trim() || null,
        is_featured: newItem.is_featured,
        sort_order: galleryItems.length,
      });
      if (response.success) {
        showToast("Image added to gallery successfully");
        setNewItem({ image_url: "", caption: "", subject: "", is_featured: false });
        loadGallery(selectedEventId);
        loadSubjects(selectedEventId);
      }
    } catch {
      showToast("Failed to add image to gallery", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAdd = async () => {
    if (!selectedEventId || uploadedImages.length === 0) {
      showToast("Please select an event and upload at least one image", "error");
      return;
    }
    setLoading(true);
    try {
      await Promise.all(
        uploadedImages.map((imageUrl, index) =>
          api.post("/api/gallery", {
            event_id: selectedEventId,
            image_url: imageUrl,
            caption: "",
            subject: bulkSubject.trim() || null,
            is_featured: false,
            sort_order: galleryItems.length + index,
          })
        )
      );
      showToast(`${uploadedImages.length} image(s) added to gallery successfully`);
      setUploadedImages([]);
      setBulkSubject("");
      loadGallery(selectedEventId);
      loadSubjects(selectedEventId);
    } catch {
      showToast("Failed to add images to gallery", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (id: number) => {
    if (!confirm("Are you sure you want to delete this image?")) return;
    setLoading(true);
    try {
      const response = await api.delete(`/api/gallery/${id}`);
      if (response.success) {
        showToast("Image deleted successfully");
        setSelectedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
        if (selectedEventId) { loadGallery(selectedEventId); loadSubjects(selectedEventId); }
      }
    } catch {
      showToast("Failed to delete image", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} selected image(s)?`)) return;
    setLoading(true);
    try {
      const response = await api.delete("/api/gallery/bulk", {
        ids: Array.from(selectedIds),
      });
      if (response.success) {
        showToast(`${selectedIds.size} image(s) deleted`);
        setSelectedIds(new Set());
        if (selectedEventId) { loadGallery(selectedEventId); loadSubjects(selectedEventId); }
      }
    } catch {
      showToast("Failed to bulk-delete images", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFeatured = async (item: GalleryItem) => {
    setLoading(true);
    try {
      const response = await api.put(`/api/gallery/${item.id}`, {
        is_featured: !item.is_featured,
      });
      if (response.success) {
        showToast(item.is_featured ? "Removed from featured" : "Marked as featured");
        if (selectedEventId) loadGallery(selectedEventId);
      }
    } catch {
      showToast("Failed to update featured status", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCaption = async (item: GalleryItem, newCaption: string) => {
    try {
      await api.put(`/api/gallery/${item.id}`, { caption: newCaption });
      if (selectedEventId) loadGallery(selectedEventId);
    } catch {
      showToast("Failed to update caption", "error");
    }
  };

  const handleUpdateSubject = async (item: GalleryItem, newSubject: string) => {
    try {
      await api.put(`/api/gallery/${item.id}`, { subject: newSubject.trim() || null });
      if (selectedEventId) { loadGallery(selectedEventId); loadSubjects(selectedEventId); }
    } catch {
      showToast("Failed to update subject", "error");
    }
  };

  const handleSaveOrder = async (orderedItems: GalleryItem[]) => {
    setSaving(true);
    try {
      const response = await api.put("/api/gallery/reorder", {
        items: orderedItems.map((item, index) => ({ id: item.id, sort_order: index })),
      });
      if (response.success) {
        showToast("Order saved successfully");
        if (selectedEventId) loadGallery(selectedEventId);
      }
    } catch {
      showToast("Failed to save order", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Accordion expand/collapse all ───────────────────────────────────────────
  const [expandAll, setExpandAll] = useState<boolean | null>(null);
  const accordionKey = expandAll === null ? "default" : expandAll ? "expanded" : "collapsed";

  // ── Subject-level drag state ──────────────────────────────────────────────
  const groupedItems = galleryItems.reduce<Record<string, GalleryItem[]>>((acc, item) => {
    const key = item.subject?.trim() || "General";
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  // Initial subject order derived from DB sort_order (groups already sorted by first item's sort_order)
  const initialSubjectOrder = Object.entries(groupedItems)
    .sort(([, aItems], [, bItems]) => (aItems[0]?.sort_order ?? 0) - (bItems[0]?.sort_order ?? 0))
    .map(([subject]) => subject);

  const [subjectOrder, setSubjectOrder] = useState<string[]>(initialSubjectOrder);
  const [subjectOrderDirty, setSubjectOrderDirty] = useState(false);
  const subjectDragIndex = useRef<number | null>(null);
  const [draggingSubjectIndex, setDraggingSubjectIndex] = useState<number | null>(null);

  // Sync subject order when gallery reloads
  useEffect(() => {
    setSubjectOrder(initialSubjectOrder);
    setSubjectOrderDirty(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryItems]);

  const handleSubjectDragStart = useCallback((index: number) => {
    subjectDragIndex.current = index;
    setDraggingSubjectIndex(index);
  }, []);

  const handleSubjectDragOver = useCallback((e: React.DragEvent, overIndex: number) => {
    e.preventDefault();
    if (subjectDragIndex.current === null || subjectDragIndex.current === overIndex) return;
    setSubjectOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(subjectDragIndex.current!, 1);
      next.splice(overIndex, 0, moved);
      subjectDragIndex.current = overIndex;
      setDraggingSubjectIndex(overIndex);
      return next;
    });
    setSubjectOrderDirty(true);
  }, []);

  const handleSubjectDragEnd = useCallback(() => {
    subjectDragIndex.current = null;
    setDraggingSubjectIndex(null);
  }, []);

  const handleResetSubjectOrder = () => {
    setSubjectOrder(initialSubjectOrder);
    setSubjectOrderDirty(false);
  };

  /** Move subject at fromIndex to a 1-based toPosition, then mark dirty */
  const handleSubjectMoveToPosition = useCallback((fromIndex: number, toPosition: number) => {
    setSubjectOrder((prev) => {
      const clampedPos = Math.max(1, Math.min(prev.length, toPosition));
      const toIndex = clampedPos - 1;
      if (toIndex === fromIndex) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setSubjectOrderDirty(true);
  }, []);

  const handleSaveSubjectOrder = async () => {
    if (!selectedEventId) return;
    setSaving(true);
    try {
      const response = await api.put("/api/gallery/reorder-subjects", {
        event_id: selectedEventId,
        subjects: subjectOrder,
      });
      if (response.success) {
        showToast("Subject order saved successfully");
        loadGallery(selectedEventId);
      }
    } catch {
      showToast("Failed to save subject order", "error");
    } finally {
      setSaving(false);
    }
  };

  // Build the final ordered groups list using subjectOrder (only subjects that actually exist)
  const subjectGroups = subjectOrder
    .filter((s) => groupedItems[s])
    .map((s) => [s, groupedItems[s]] as [string, GalleryItem[]]);

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl text-primary mb-2">Gallery Management</h1>
        <p className="text-muted-foreground">Manage event photo gallery</p>
      </div>

      {/* Event selector */}
      <Card className="p-6">
        <div>
          <Label htmlFor="event">Select Event</Label>
          <Select
            value={selectedEventId?.toString() || ""}
            onValueChange={(value) => setSelectedEventId(parseInt(value))}
          >
            <SelectTrigger id="event" className="mt-1">
              <SelectValue placeholder="Select an event" />
            </SelectTrigger>
            <SelectContent>
              {events.map((event) => (
                <SelectItem key={event.id} value={event.id.toString()}>
                  {event.title}
                  {event.event_date && ` (${event.event_date})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {selectedEventId && (
        <>
          {/* ── Add New Images ─────────────────────────────────────────────── */}
          <Card className="p-6">
            <h2 className="font-semibold text-lg mb-4">Add New Images</h2>
            <div className="space-y-4">

              {/* File upload */}
              <div>
                <Label htmlFor="image-upload">Upload Images (Multiple)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" size="icon" disabled={uploading}>
                    <Upload className="w-4 h-4" />
                  </Button>
                </div>
                {uploading && <p className="text-sm text-muted-foreground mt-1">Uploading…</p>}
              </div>

              {/* Bulk preview */}
              {uploadedImages.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Uploaded Images ({uploadedImages.length})</Label>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setUploadedImages([])}>
                      Clear All
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {uploadedImages.map((imageUrl, index) => (
                      <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-secondary">
                        <img src={imageUrl} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute top-1 right-1 h-6 w-6"
                          onClick={() => setUploadedImages(uploadedImages.filter((_, i) => i !== index))}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <div>
                    <Label htmlFor="bulk-subject" className="flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5" /> Subject / Category for all uploaded images
                    </Label>
                    <Input
                      id="bulk-subject"
                      list="subject-suggestions"
                      value={bulkSubject}
                      onChange={(e) => setBulkSubject(e.target.value)}
                      placeholder="e.g. Opening Ceremony, Workshop, Networking…"
                      className="mt-1"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Leave blank to use "General".
                    </p>
                  </div>

                  <Button onClick={handleBulkAdd} disabled={loading} className="w-full">
                    {loading ? "Adding…" : `Add All ${uploadedImages.length} Images to Gallery`}
                  </Button>
                </div>
              )}

              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground mb-3">Or add a single image with caption and subject:</p>
              </div>

              {newItem.image_url && (
                <div className="relative">
                  <img src={newItem.image_url} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2"
                    onClick={() => setNewItem({ ...newItem, image_url: "" })}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}

              <div>
                <Label htmlFor="new-subject" className="flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5" /> Subject / Category
                </Label>
                <Input
                  id="new-subject"
                  list="subject-suggestions"
                  value={newItem.subject}
                  onChange={(e) => setNewItem({ ...newItem, subject: e.target.value })}
                  placeholder="e.g. Opening Ceremony, Workshop, Networking…"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="caption">Caption (optional)</Label>
                <Textarea
                  id="caption"
                  value={newItem.caption}
                  onChange={(e) => setNewItem({ ...newItem, caption: e.target.value })}
                  placeholder="Enter image caption"
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="featured"
                  checked={newItem.is_featured}
                  onChange={(e) => setNewItem({ ...newItem, is_featured: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <Label htmlFor="featured" className="cursor-pointer">Mark as featured</Label>
              </div>

              <Button onClick={handleAddItem} disabled={loading || !newItem.image_url} className="w-full">
                {loading ? "Adding…" : "Add Single Image to Gallery"}
              </Button>
            </div>
          </Card>

          {/* Datalist shared across all subject inputs */}
          <datalist id="subject-suggestions">
            {existingSubjects.map((s) => <option key={s} value={s} />)}
          </datalist>

          {/* ── Gallery Images ────────────────────────────────────────────── */}
          <Card className="p-6">
            {/* Header + bulk toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="font-semibold text-lg">
                Gallery Images ({galleryItems.length})
              </h2>

              {galleryItems.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Expand / Collapse all */}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setExpandAll(true)}
                    className="gap-1.5"
                    title="Expand all subjects"
                  >
                    <ChevronsUpDown className="w-3.5 h-3.5" />
                    Expand All
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setExpandAll(false)}
                    className="gap-1.5"
                    title="Collapse all subjects"
                  >
                    <ChevronsDownUp className="w-3.5 h-3.5" />
                    Collapse All
                  </Button>

                  <div className="w-px h-5 bg-border" />

                  {selectedIds.size > 0 ? (
                    <>
                      <span className="text-sm text-muted-foreground">
                        {selectedIds.size} selected
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={clearSelection}
                      >
                        Clear
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={handleBulkDelete}
                        disabled={loading}
                        className="gap-1.5"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete {selectedIds.size} image{selectedIds.size !== 1 ? "s" : ""}
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={selectAll}
                      className="gap-1.5"
                    >
                      <CheckSquare className="w-3.5 h-3.5" />
                      Select All
                    </Button>
                  )}
                </div>
              )}
            </div>

            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading…</div>
            ) : galleryItems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No images in gallery yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Subject order dirty banner */}
                {subjectOrderDirty && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <GripVertical className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <span className="text-sm text-blue-700 flex-1">
                      Subject order changed — save to persist.
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleResetSubjectOrder}
                      disabled={saving}
                      className="text-blue-700 hover:text-blue-900 hover:bg-blue-100"
                    >
                      Reset
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSaveSubjectOrder}
                      disabled={saving}
                      className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Save className="w-3.5 h-3.5" />
                      {saving ? "Saving…" : "Save Subject Order"}
                    </Button>
                  </div>
                )}

                {subjectGroups.map(([subject, items], index) => (
                  <SubjectGroup
                    key={`${accordionKey}-${subject}`}
                    subject={subject}
                    initialItems={items}
                    selectedIds={selectedIds}
                    defaultOpen={expandAll === null ? true : expandAll}
                    subjectPosition={index + 1}
                    totalSubjects={subjectGroups.length}
                    subjectIndex={index}
                    isDraggingSubject={draggingSubjectIndex !== null && draggingSubjectIndex !== index}
                    onSubjectDragStart={handleSubjectDragStart}
                    onSubjectDragOver={handleSubjectDragOver}
                    onSubjectDragEnd={handleSubjectDragEnd}
                    onSubjectMoveToPosition={handleSubjectMoveToPosition}
                    onToggleSelect={toggleSelect}
                    onToggleFeatured={handleToggleFeatured}
                    onDelete={handleDeleteItem}
                    onUpdateSubject={handleUpdateSubject}
                    onUpdateCaption={handleUpdateCaption}
                    onSaveOrder={handleSaveOrder}
                    saving={saving}
                  />
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
